import {
  Fragment,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { SingleValue } from 'react-select';
import Dropdown from '../form-inputs/inputDropdown';
import GetAdminFilters from '../../services/adapters/adminFilters';
import GetAdminPostHogFeatureFlags from '../../services/adapters/adminPostHogFeatureFlags';
import UpdateAdminFilterFeatureFlag from '../../services/adapters/updateAdminFilterFeatureFlag';
import { PostHogFeatureFlagResponse } from '../../api-models';
import type { OptionType } from '../../types/forms.type';
import { FilterAdmin } from '../../types/filters.type';

const UnsetOption: OptionType = { label: '— None —', value: '' };
const TableColumnCount = 3;

export default function FilterAdminPanel() {
  const [filters, setFilters] = useState<FilterAdmin[]>([]);
  const [flags, setFlags] = useState<PostHogFeatureFlagResponse[]>([]);
  const [savingFilterId, setSavingFilterId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const flagOptions = useMemo<OptionType[]>(
    () => [
      UnsetOption,
      ...flags.map((flag) => ({
        label: flag.name ? `${flag.key} — ${flag.name}${flag.active ? '' : ' (inactive)'}` : flag.key,
        value: flag.key,
      })),
    ],
    [flags],
  );

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const [filtersResponse, flagsResponse] = await Promise.all([
          new GetAdminFilters().adaptResponse(),
          new GetAdminPostHogFeatureFlags().adaptResponse().catch(() => [] as PostHogFeatureFlagResponse[]),
        ]);
        setFilters(filtersResponse);
        setFlags(flagsResponse);
      } catch (loadError) {
        setError(loadError instanceof Error ? loadError.message : 'Failed to load filter admin data');
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, []);

  const handleFlagChange = async (filter: FilterAdmin, value: SingleValue<OptionType>) => {
    const newFlag = value ? value.value : '';
    if (newFlag === (filter.featureFlag ?? '')) {
      return;
    }
    try {
      setSavingFilterId(filter.id);
      const updated = await new UpdateAdminFilterFeatureFlag().adaptResponse(filter.id, newFlag || null);
      setFilters((prev) => prev.map((existing) => (existing.id === updated.id ? updated : existing)));
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Failed to save filter feature flag');
    } finally {
      setSavingFilterId(null);
    }
  };

  if (loading) {
    return <p className="text-secondary p-2">Loading filters…</p>;
  }

  return (
    <div className="border border-pipgrey-light rounded-md p-2 overflow-auto">
      {error && <p className="text-red-600 text-sm p-2">{error}</p>}
      <p className="text-xs text-secondary p-2">
        Payload markdown is configured on the feature flag itself in PostHog and only renders when the flag is active for the viewing user.
      </p>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-secondary border-b">
            <th className="p-2 w-1/3">Filter</th>
            <th className="p-2 w-1/3">Description</th>
            <th className="p-2 w-1/3">Feature flag</th>
          </tr>
        </thead>
        <tbody>
          {filters.map((filter, index) => {
            const previousFilter = index > 0 ? filters[index - 1] : null;
            const hasCategoryChanged = previousFilter === null
    || filter.categoryName !== previousFilter.categoryName;

            const subcategoryKey = `${filter.categoryName}/${filter.subcategoryName}`;
            const lastSubcategoryKey = previousFilter
              ? `${previousFilter.categoryName}/${previousFilter.subcategoryName}`
              : null;

            const hasSubcategoryChanged = hasCategoryChanged || subcategoryKey !== lastSubcategoryKey;

            return (
              <Fragment key={filter.id}>
                {hasCategoryChanged && (
                  <tr className="bg-primary-dark">
                    <td colSpan={TableColumnCount} className="p-2 font-semibold text-white text-base">
                      {filter.categoryDisplayName}
                    </td>
                  </tr>
                )}
                {hasSubcategoryChanged && (() => {
                  const hasSubcategory = Boolean(filter.subcategoryDisplayName?.trim());
                  return (
                    <tr>
                      <td
                        colSpan={TableColumnCount}
                        className={`pt-4 pb-2 px-4 border-l-4 border-primary bg-pipgrey-light font-semibold text-sm uppercase tracking-wide ${
                          hasSubcategory ? 'text-primary-dark' : 'italic text-pipgrey-md'
                        }`}
                      >
                        {hasSubcategory ? filter.subcategoryDisplayName : 'No Sub-category'}
                      </td>
                    </tr>
                  );
                })()}
                <tr className="border-b align-top">
                  <td className="p-2 ps-8">
                    <div className="font-semibold text-piptext-default">{filter.displayName}</div>
                    <div className="text-xs text-secondary">{filter.name}</div>
                  </td>
                  <td className="p-2 text-piptext-body">{filter.description ?? '—'}</td>
                  <td className="p-2">
                    <Dropdown
                      id={`filter-admin-flag-${filter.id}`}
                      label={savingFilterId === filter.id ? 'Saving…' : ''}
                      placement="auto"
                      onChange={(_id, value) => handleFlagChange(filter, value)}
                      selected={filter.featureFlag ?? ''}
                      options={flagOptions}
                    />
                  </td>
                </tr>
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
